/**
 * @fileoverview
 * Token Service - Manages JWT tokens
 *
 * @version 1.0.0
 * @author KATALYST TEAM
 */

import { Injectable } from '@angular/core';
import { StorageUtil } from '../../utils/storage.utils';
import { STORAGE_KEYS } from '../../constants/storage-keys.const';

@Injectable({
  providedIn: 'root',
})
export class TokenService {
  /**
   * Get access token from storage
   */
  getToken(): string | null {
    return StorageUtil.getLocal<string>(STORAGE_KEYS.ACCESS_TOKEN);
  }

  /**
   * Save access token to storage
   */
  setToken(token: string): void {
    StorageUtil.setLocal(STORAGE_KEYS.ACCESS_TOKEN, token);
  }

  /**
   * Get refresh token from storage
   */
  getRefreshToken(): string | null {
    return StorageUtil.getLocal<string>(STORAGE_KEYS.REFRESH_TOKEN);
  }

  /**
   * Save refresh token to storage
   */
  setRefreshToken(token: string): void {
    StorageUtil.setLocal(STORAGE_KEYS.REFRESH_TOKEN, token);
  }

  /**
   * Remove all tokens from storage
   */
  removeTokens(): void {
    StorageUtil.removeLocal(STORAGE_KEYS.ACCESS_TOKEN);
    StorageUtil.removeLocal(STORAGE_KEYS.REFRESH_TOKEN);
  }

  /**
   * Check if token exists
   */
  hasToken(): boolean {
    return !!this.getToken();
  }

  /**
   * Decode JWT token (without verification)
   */
  decodeToken(token: string): any {
    try {
      const payload = token.split('.')[1];
      if (!payload) return null;
      const base64 = payload.replaceAll('-', '+').replaceAll('_', '/');
      const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
      const decoded = atob(padded);
      return JSON.parse(decoded);
    } catch (error) {
      console.error('Error decoding token:', error);
      return null;
    }
  }

  /**
   * Check if token is expired
   */
  isTokenExpired(token?: string): boolean {
    const tokenToCheck = token ?? this.getToken();

    if (!tokenToCheck) {
      return true;
    }

    const decoded = this.decodeToken(tokenToCheck);

    if (!decoded?.exp) {
      return true;
    }

    const expirationDate = new Date(decoded.exp * 1000);
    const now = new Date();

    return expirationDate <= now;
  }

  /**
   * Get token expiration time
   */
  getTokenExpirationTime(token?: string): Date | null {
    const tokenToCheck = token ?? this.getToken();

    if (!tokenToCheck) {
      return null;
    }

    const decoded = this.decodeToken(tokenToCheck);

    if (!decoded?.exp) {
      return null;
    }

    return new Date(decoded.exp * 1000);
  }

  /**
   * Get user ID from token
   */
  getUserIdFromToken(token?: string): number | null {
    const tokenToCheck = token ?? this.getToken();

    if (!tokenToCheck) {
      return null;
    }

    const decoded = this.decodeToken(tokenToCheck);
    return decoded?.sub || null;
  }
}
